import React, { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import {
  notificationService,
  UserNotificationSettings as UserNotificationSettingsType,
  UserSettingsFilters,
} from '../services/notificationService';

const UserNotificationSettings: React.FC = () => {
  const [users, setUsers] = useState<UserNotificationSettingsType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserSettingsFilters>({
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [selectedUser, setSelectedUser] = useState<UserNotificationSettingsType | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [search, setSearch] = useState('');

  const loadUsers = useCallback(
    async (page = 1, searchTerm = '', append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setUsers([]);
        }

        const searchFilters = {
          ...filters,
          page: page,
          search: searchTerm,
        };

        const data = await notificationService.getUserSettings(searchFilters);

        if (append) {
          setUsers((prev) => [...prev, ...data.users]);
        } else {
          setUsers(data.users);
        }

        setPagination(data.pagination);
        setHasMore(data.users.length === (filters.limit || 20));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user settings');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters]
  );

  const loadMoreUsers = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = pagination.page + 1;
      loadUsers(nextPage, search, true);
    }
  }, [loadingMore, hasMore, pagination.page, search, loadUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setHasMore(true);
    loadUsers(1, search, false);
  };

  const handleFilterChange = (newFilters: Partial<UserSettingsFilters>) => {
    setHasMore(true);
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    loadUsers(1, search, false);
  };

  const handleEditSettings = (user: UserNotificationSettingsType) => {
    setSelectedUser(user);
    setShowSettingsModal(true);
  };

  const handleUpdateSettings = async (userId: string, settings: any) => {
    try {
      await notificationService.updateUserSettings(userId, settings);
      await loadUsers(1, search, false);
      setShowSettingsModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user settings');
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">사용자 알림 설정</h1>
      </div>

      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">사용자 검색</label>
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="닉네임 또는 이메일로 검색..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">페이지당 표시</label>
            <select
              value={filters.limit || 20}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setHasMore(true);
                loadUsers(1, '', false);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 사용자 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  푸시 알림
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  알림 타입
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  조용한 시간
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  활성 토큰
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.nickname || '-'}</div>
                    <div className="text-sm text-gray-500">{user.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.pushNotificationEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.pushNotificationEnabled ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.basicNotifications ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        📱 기본
                      </span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.promotionNotifications ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        🎁 프로모션
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.quietHours?.enabled ? (
                      <div>
                        <div className="text-xs text-gray-500">활성</div>
                        <div className="text-sm">
                          {user.quietHours.start} - {user.quietHours.end}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">비활성</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.activeTokensCount > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.activeTokensCount}개
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleEditSettings(user)} className="text-blue-600 hover:text-blue-900">
                      설정 수정
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 더 보기 버튼 및 통계 */}
        <div className="bg-white px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-700">
                총 <span className="font-medium">{pagination.total}</span>명 중{' '}
                <span className="font-medium">{users.length}</span>명 표시
              </p>
            </div>
            <div>
              {loadingMore && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>더 불러오는
                  중...
                </div>
              )}
            </div>
          </div>

          {/* 더 보기 버튼 */}
          {hasMore && !loadingMore && (
            <div className="text-center">
              <button
                onClick={loadMoreUsers}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                더 보기
              </button>
            </div>
          )}

          {!hasMore && users.length > 0 && (
            <div className="text-center text-sm text-gray-500 py-2">모든 사용자를 불러왔습니다.</div>
          )}
        </div>
      </div>

      {/* 설정 수정 모달 */}
      {showSettingsModal && selectedUser && (
        <UserSettingsModal
          user={selectedUser}
          onClose={() => {
            setShowSettingsModal(false);
            setSelectedUser(null);
          }}
          onSave={handleUpdateSettings}
        />
      )}
    </div>
  );
};

// 사용자 설정 수정 모달 컴포넌트
const UserSettingsModal: React.FC<{
  user: UserNotificationSettingsType;
  onClose: () => void;
  onSave: (userId: string, settings: any) => void;
}> = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    pushNotification: user.pushNotificationEnabled,
    basicNotifications: user.basicNotifications ?? true,
    promotionNotifications: user.promotionNotifications ?? false,
    notificationCategories: user.notificationCategories,
    quietHours: {
      enabled: user.quietHours?.enabled || false,
      start: user.quietHours?.start || '22:00',
      end: user.quietHours?.end || '08:00',
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await onSave(user.id, formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">알림 설정 수정 - {user.nickname}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 푸시 알림 활성화 */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.pushNotification}
                  onChange={(e) => setFormData((prev) => ({ ...prev, pushNotification: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">푸시 알림 받기</span>
              </label>
            </div>

            {/* 알림 타입 설정 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">알림 타입 설정</label>
              <div className="space-y-4">
                {/* 기본 알림 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">📱</span>
                      <span className="font-medium text-gray-900">기본 알림</span>
                      <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">필수</span>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.basicNotifications}
                        onChange={(e) => setFormData((prev) => ({ ...prev, basicNotifications: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">서비스 점검, 보안 알림, 분석 완료 등</p>
                </div>

                {/* 프로모션 알림 */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-lg mr-2">🎁</span>
                      <span className="font-medium text-gray-900">프로모션 알림</span>
                      <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">선택</span>
                    </div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.promotionNotifications}
                        onChange={(e) => setFormData((prev) => ({ ...prev, promotionNotifications: e.target.checked }))}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">이벤트, 할인, 신규 기능 홍보 등</p>
                </div>
              </div>
            </div>

            {/* 조용한 시간 설정 */}
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.quietHours.enabled}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quietHours: { ...prev.quietHours, enabled: e.target.checked },
                    }))
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">조용한 시간 설정</span>
              </label>

              {formData.quietHours.enabled && (
                <div className="ml-6 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">시작 시간</label>
                    <input
                      type="time"
                      value={formData.quietHours.start}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, start: e.target.value },
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">종료 시간</label>
                    <input
                      type="time"
                      value={formData.quietHours.end}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, end: e.target.value },
                        }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserNotificationSettings;
